<template>
  <v-app class="portal-app" :class="{ 'portal-app--dark': isDarkTheme }">
    <PortalSnackBar />
    <v-snackbar
      v-model="showPwaInstallPrompt"
      class="portal-pwa-install-snackbar"
      color="surface"
      :location="pwaInstallSnackbarLocation"
      :timeout="-1"
      multi-line
    >
      <div class="portal-pwa-install">
        <v-icon
          class="portal-pwa-install__icon"
          icon="mdi-cellphone-arrow-down"
          color="primary"
          size="28"
        />
        <div class="portal-pwa-install__content">
          <p class="portal-pwa-install__title mb-1">
            Instale la aplicación del portal
          </p>
          <p class="portal-pwa-install__text mb-0">
            {{ pwaInstallText }}
          </p>
        </div>
      </div>
      <template #actions>
        <div class="portal-pwa-install__actions">
          <v-btn
            v-if="showPwaInstallAction"
            color="primary"
            variant="flat"
            size="small"
            @click="installPwa"
          >
            Instalar
          </v-btn>
          <v-btn
            color="primary"
            variant="text"
            size="small"
            @click="dismissPwaInstallPrompt"
          >
            Ahora no
          </v-btn>
        </div>
      </template>
    </v-snackbar>
    <div
      class="portal-app-chrome d-flex flex-column flex-grow-1 min-w-0"
      :class="{ 'portal-app-chrome--auth': isAuthFullscreen }"
    >
      <router-view
        class="portal-app-chrome__view flex-grow-1 d-flex flex-column min-h-0 min-w-0"
      />
      <template v-if="!isAuthFullscreen">
        <PortalInstitutionalFooter />
        <v-divider></v-divider>
        <PortalProductAttribution />
      </template>
    </div>
  </v-app>
</template>

<script>
import PortalSnackBar from './components/layout/PortalSnackBar.vue'
import PortalInstitutionalFooter from './components/layout/PortalInstitutionalFooter.vue'
import PortalProductAttribution from './components/layout/PortalProductAttribution.vue'
import api, { getPortalAccessMode } from './axios/axios-api'
import { clearDarkModePreference } from './utils/helpers/portalDarkModePreference'

const AUTH_FULLSCREEN_PATHS = ['/login', '/validar-cuenta', '/validar-cambio-email', '/restablecer-contrasena']
const PWA_INSTALL_PENDING_KEY = 'portalPwaInstallPending'
const PWA_INSTALL_LOGGED_KEY = 'portalPwaInstallLogged'
const PWA_INSTALLED_ACCESS_MODE = 'pwa_standalone'

export default {
  name: 'App',
  components: {
    PortalSnackBar,
    PortalInstitutionalFooter,
    PortalProductAttribution,
  },
  data() {
    return {
      deferredPwaInstallPrompt: null,
      showPwaInstallPrompt: false,
    }
  },
  computed: {
    isAuthFullscreen() {
      return AUTH_FULLSCREEN_PATHS.includes(this.$route.path)
    },
    canTriggerPwaInstall() {
      return !!this.deferredPwaInstallPrompt
    },
    showPwaInstallAction() {
      return !this.isIosDevice && this.canTriggerPwaInstall
    },
    isIosDevice() {
      if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
      const ua = navigator.userAgent || ''
      const platform = navigator.platform || ''
      return /iPad|iPhone|iPod/.test(ua)
        || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)
    },
    isIosSafari() {
      if (!this.isIosDevice || typeof navigator === 'undefined') return false
      const ua = navigator.userAgent || ''
      const isSafari = /Safari/i.test(ua)
      const isOtherIosBrowser = /CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo/i.test(ua)
      return isSafari && !isOtherIosBrowser
    },
    isStandalonePwa() {
      if (typeof window === 'undefined') return false
      return getPortalAccessMode() !== 'browser'
        && getPortalAccessMode() !== 'unknown'
    },
    pwaInstallText() {
      if (this.isIosDevice) {
        if (!this.isIosSafari) {
          return 'Para instalar este portal con IOS, se debe abrir desde Safari.'
        }
        return 'En Safari, toque Compartir y elija "Agregar a inicio".'
      }
      return 'Instale la aplicación para acceder más rápido desde la pantalla de inicio.'
    },
    pwaInstallSnackbarLocation() {
      return this.$vuetify?.display?.smAndDown ? 'top' : 'bottom'
    },
    isDarkTheme() {
      const themeName = this.$vuetify?.theme?.global?.name
      const value = themeName && typeof themeName === 'object'
        ? themeName.value
        : themeName
      return value === 'dark'
    },
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler() {
        const on = AUTH_FULLSCREEN_PATHS.includes(this.$route.path)
        document.documentElement.classList.toggle('portal-auth-fullscreen', on)
        this.marcarPwaInstalledPendienteDesdeStandalone()
        this.registrarPwaInstalledPendiente()
      },
    },
  },
  mounted() {
    this.setupPwaInstallPrompt()
    this.marcarPwaInstalledPendienteDesdeStandalone()
    this.registrarPwaInstalledPendiente()
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    this._portalColorSchemeMql = window.matchMedia('(prefers-color-scheme: dark)')
    this._portalColorSchemeHandler = (event) => {
      clearDarkModePreference()
      this.applySystemDarkMode(event.matches)
    }
    if (typeof this._portalColorSchemeMql.addEventListener === 'function') {
      this._portalColorSchemeMql.addEventListener('change', this._portalColorSchemeHandler)
    } else if (typeof this._portalColorSchemeMql.addListener === 'function') {
      this._portalColorSchemeMql.addListener(this._portalColorSchemeHandler)
    }
  },
  beforeUnmount() {
    window.removeEventListener('beforeinstallprompt', this.onBeforeInstallPrompt)
    window.removeEventListener('appinstalled', this.onPwaInstalled)
    document.documentElement.classList.remove('portal-auth-fullscreen')
    if (!this._portalColorSchemeMql || !this._portalColorSchemeHandler) return
    if (typeof this._portalColorSchemeMql.removeEventListener === 'function') {
      this._portalColorSchemeMql.removeEventListener('change', this._portalColorSchemeHandler)
    } else if (typeof this._portalColorSchemeMql.removeListener === 'function') {
      this._portalColorSchemeMql.removeListener(this._portalColorSchemeHandler)
    }
  },
  methods: {
    setupPwaInstallPrompt() {
      if (typeof window === 'undefined') return
      if (this.isStandalonePwa || sessionStorage.getItem('portalPwaInstallDismissed') === '1') return

      window.addEventListener('beforeinstallprompt', this.onBeforeInstallPrompt)
      window.addEventListener('appinstalled', this.onPwaInstalled)

      this.showPwaInstallPrompt = this.isIosDevice
    },
    onBeforeInstallPrompt(event) {
      event.preventDefault()
      this.deferredPwaInstallPrompt = event
      if (!this.isStandalonePwa) {
        this.showPwaInstallPrompt = true
      }
    },
    onPwaInstalled() {
      this.deferredPwaInstallPrompt = null
      this.showPwaInstallPrompt = false
      sessionStorage.setItem('portalPwaInstallDismissed', '1')
      sessionStorage.setItem('portalAccessMode', PWA_INSTALLED_ACCESS_MODE)
      this.marcarPwaInstalledPendiente()
      this.registrarPwaInstalledPendiente()
    },
    marcarPwaInstalledPendiente(accessMode = PWA_INSTALLED_ACCESS_MODE) {
      if (typeof window === 'undefined') return
      if (localStorage.getItem(PWA_INSTALL_LOGGED_KEY) === '1') return
      localStorage.setItem(PWA_INSTALL_PENDING_KEY, JSON.stringify({
        installedAt: new Date().toISOString(),
        accessMode,
        userAgent: navigator?.userAgent || '',
      }))
    },
    marcarPwaInstalledPendienteDesdeStandalone() {
      if (typeof window === 'undefined') return
      if (localStorage.getItem(PWA_INSTALL_LOGGED_KEY) === '1') return
      if (localStorage.getItem(PWA_INSTALL_PENDING_KEY)) return

      const accessMode = getPortalAccessMode()
      if (accessMode === 'browser' || accessMode === 'unknown') return

      this.marcarPwaInstalledPendiente(accessMode)
    },
    async registrarPwaInstalledPendiente() {
      if (typeof window === 'undefined') return
      if (localStorage.getItem(PWA_INSTALL_LOGGED_KEY) === '1') return
      const pendingRaw = localStorage.getItem(PWA_INSTALL_PENDING_KEY)
      if (!pendingRaw) return

      let pending = null
      try {
        pending = JSON.parse(pendingRaw)
      } catch {
        pending = null
      }

      const accessMode = pending?.accessMode || PWA_INSTALLED_ACCESS_MODE
      try {
        await api.post('LogPortalPaciente', {
          module: 'PWA',
          endpoint: 'PWA/Installed',
          payload: JSON.stringify({
            standalone: true,
            accessMode,
            installedAt: pending?.installedAt || null,
            userAgent: pending?.userAgent || navigator?.userAgent || '',
          }),
          response: JSON.stringify({ success: true }),
          accessMode,
          observaciones: 'Instalacion de Web App del portal paciente',
        }, { skipGlobalErrorAlert: true })
        localStorage.setItem(PWA_INSTALL_LOGGED_KEY, '1')
        localStorage.removeItem(PWA_INSTALL_PENDING_KEY)
      } catch {
        // No bloquear la experiencia de instalacion por metricas.
      }
    },
    async installPwa() {
      if (this.isIosDevice && !this.deferredPwaInstallPrompt) return
      if (!this.deferredPwaInstallPrompt) return
      const promptEvent = this.deferredPwaInstallPrompt
      this.deferredPwaInstallPrompt = null
      await promptEvent.prompt()
      const choice = await promptEvent.userChoice.catch(() => null)
      if (choice?.outcome === 'accepted') {
        this.showPwaInstallPrompt = false
        sessionStorage.setItem('portalPwaInstallDismissed', '1')
      }
    },
    dismissPwaInstallPrompt() {
      this.showPwaInstallPrompt = false
      sessionStorage.setItem('portalPwaInstallDismissed', '1')
    },
    applySystemDarkMode(value) {
      const themeName = value ? 'dark' : 'light'
      if (typeof this.$vuetify?.theme?.change === 'function') {
        this.$vuetify.theme.change(themeName)
      } else {
        const themeRef = this.$vuetify?.theme?.global?.name
        if (themeRef && typeof themeRef === 'object' && 'value' in themeRef) {
          themeRef.value = themeName
        }
      }
      document.documentElement.classList.toggle('portal-dark-mode', !!value)
    },
  },
}
</script>

<style>
/* Campos de solo lectura/deshabilitados: similar a filled de Vuetify 2, sin perder contraste. */
.portal-app
  :is(.v-text-field, .v-select, .v-autocomplete, .v-combobox, .v-textarea)
  .v-field:is(.v-field--readonly, .v-field--disabled) {
  background-color: #eeeeee !important;
  opacity: 1 !important;
}

.portal-app .v-text-field:has(input[readonly]) .v-field {
  background-color: #eeeeee !important;
  opacity: 1 !important;
}

.portal-app--dark
  :is(.v-text-field, .v-select, .v-autocomplete, .v-combobox, .v-textarea)
  .v-field:is(.v-field--readonly, .v-field--disabled),
.portal-app--dark .v-text-field:has(input[readonly]) .v-field {
  background-color: #26313c !important;
}

.portal-app
  :is(.v-text-field, .v-select, .v-autocomplete, .v-combobox, .v-textarea)
  .v-field:is(.v-field--readonly, .v-field--disabled)
  .v-field__overlay {
  background-color: transparent !important;
  opacity: 0 !important;
}

.portal-app .v-text-field:has(input[readonly]) .v-field__overlay {
  background-color: transparent !important;
  opacity: 0 !important;
}

.portal-table-actions {
  column-gap: 12px;
  row-gap: 6px;
}

.portal-table-action-btn .v-btn__prepend {
  margin-inline-end: 4px;
}

.portal-table-action-btn .v-btn__prepend .v-icon {
  font-size: 12px;
  height: 12px;
  width: 12px;
}

.portal-app
  :is(.v-text-field, .v-select, .v-autocomplete, .v-combobox, .v-textarea)
  .v-field:is(.v-field--readonly, .v-field--disabled)
  :is(.v-field__input, .v-select__selection, .v-autocomplete__selection, input, textarea) {
  color: rgba(var(--v-theme-on-surface), 0.87) !important;
  opacity: 1 !important;
}

.portal-app
  .v-text-field:has(input[readonly])
  :is(.v-field__input, input) {
  color: rgba(var(--v-theme-on-surface), 0.87) !important;
  opacity: 1 !important;
}

.portal-app
  :is(.v-text-field, .v-select, .v-autocomplete, .v-combobox, .v-textarea)
  .v-field:is(.v-field--readonly, .v-field--disabled)
  :is(.v-label, .v-field__append-inner, .v-field__prepend-inner, .v-field__clearable) {
  opacity: 1 !important;
}

.portal-app
  .v-text-field:has(input[readonly])
  :is(.v-label, .v-field__append-inner, .v-field__prepend-inner, .v-field__clearable) {
  opacity: 1 !important;
}

.portal-app--dark
  :is(.v-text-field, .v-select, .v-autocomplete, .v-combobox, .v-textarea)
  .v-field:is(.v-field--readonly, .v-field--disabled)
  :is(.v-field__prepend-inner, .v-field__append-inner, .v-field__prefix, .v-field__suffix),
.portal-app--dark
  .v-text-field:has(input[readonly])
  :is(.v-field__prepend-inner, .v-field__append-inner, .v-field__prefix, .v-field__suffix) {
  color: rgba(var(--v-theme-on-surface), 0.92) !important;
  opacity: 1 !important;
}

.portal-app
  :is(.v-text-field, .v-select, .v-autocomplete, .v-combobox, .v-textarea)
  .v-field:is(.v-field--readonly, .v-field--disabled)
  .v-field__outline {
  --v-field-border-opacity: 0.42;
}

.portal-app .v-text-field:has(input[readonly]) .v-field__outline {
  --v-field-border-opacity: 0.42;
}

.portal-app--dark
  :is(.v-text-field, .v-select, .v-autocomplete, .v-combobox, .v-textarea)
  .v-field:is(.v-field--readonly, .v-field--disabled)
  .v-field__outline,
.portal-app--dark .v-text-field:has(input[readonly]) .v-field__outline {
  --v-field-border-opacity: 0.5;
}

.portal-app .v-application__wrap {
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  min-width: 0;
  background: rgb(var(--v-theme-background));
}
@supports not (min-height: 100dvh) {
  .portal-app .v-application__wrap {
    min-height: 100vh;
  }
}
.portal-app-chrome:not(.portal-app-chrome--auth) {
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
}
.portal-app-chrome__product-divider {
  border-color: rgba(0, 0, 0, 0.08) !important;
}
.portal-app--dark .portal-app-chrome__product-divider {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.portal-app.portal-app--dark .v-data-table thead th,
.portal-app.portal-app--dark .v-data-table thead .v-data-table__th,
.portal-app.portal-app--dark .v-data-table .v-data-table-header__content {
  color: #fff !important;
}

.portal-app.portal-app--dark .v-card {
  border-color: rgba(255, 255, 255, 0.1) !important;
}

.portal-app .v-btn.bg-primary:not(.v-btn--disabled) {
  color: rgb(var(--v-theme-on-primary)) !important;
}

.portal-app .v-btn.bg-secondary:not(.v-btn--disabled) {
  color: rgb(var(--v-theme-on-secondary)) !important;
}

.portal-pwa-install {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  max-width: 560px;
}

.portal-pwa-install__icon {
  flex: 0 0 auto;
  margin-top: 2px;
}

.portal-pwa-install__content {
  min-width: 0;
}

.portal-pwa-install__title {
  color: rgba(var(--v-theme-on-surface), 0.92);
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1.25;
}

.portal-pwa-install__text {
  color: rgba(var(--v-theme-on-surface), 0.74);
  font-size: 0.86rem;
  line-height: 1.35;
}

.portal-pwa-install__actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.portal-pwa-install-snackbar :deep(.v-snackbar__wrapper) {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}

.portal-pwa-install-snackbar :deep(.v-snackbar__content) {
  min-width: 0;
}

.portal-pwa-install-snackbar :deep(.v-snackbar__actions) {
  align-self: center;
  gap: 4px;
  margin-inline-start: 8px;
}

@media (max-width: 480px) {
  .portal-pwa-install-snackbar :deep(.v-snackbar__wrapper) {
    align-items: stretch;
    margin-bottom: 28px;
  }

  .portal-pwa-install-snackbar :deep(.v-snackbar__actions) {
    align-items: flex-end;
    flex-direction: column;
    justify-content: flex-end;
    margin-inline-start: 0;
    padding-top: 0;
  }

  .portal-pwa-install-snackbar :deep(.v-snackbar__actions .v-btn) {
    margin: 0;
  }

  .portal-pwa-install__actions {
    align-items: flex-end;
    flex-direction: column;
  }

  .portal-pwa-install__actions .v-btn {
    margin: 0;
  }
}

html.portal-auth-fullscreen,
html.portal-auth-fullscreen body {
  height: 100vh;
  min-height: 100vh;
  overflow: hidden;
}
html.portal-auth-fullscreen #app {
  height: 100vh;
  min-height: 100vh;
}
html.portal-auth-fullscreen .portal-app.v-application {
  height: 100vh;
  min-height: 100vh;
}
html.portal-auth-fullscreen .portal-app .v-application__wrap {
  min-height: 100vh !important;
  height: 100vh;
}
html.portal-auth-fullscreen .portal-app-chrome--auth {
  flex: 1 1 auto;
  min-height: 0;
  height: 100vh;
}
html.portal-auth-fullscreen .portal-app .v-main {
  flex: 1 1 auto !important;
  min-height: 0 !important;
  max-height: 100vh;
}
@supports (height: 100dvh) {
  html.portal-auth-fullscreen,
  html.portal-auth-fullscreen body,
  html.portal-auth-fullscreen #app,
  html.portal-auth-fullscreen .portal-app.v-application {
    height: 100dvh;
    min-height: 100dvh;
  }

  html.portal-auth-fullscreen .portal-app .v-application__wrap {
    min-height: 100dvh !important;
    height: 100dvh;
  }

  html.portal-auth-fullscreen .portal-app-chrome--auth {
    height: 100dvh;
  }

  html.portal-auth-fullscreen .portal-app .v-main {
    max-height: 100dvh;
  }
}
@media (max-width: 1279px) {
  html.portal-auth-fullscreen,
  html.portal-auth-fullscreen body {
    height: auto;
    min-height: 100%;
    overflow: auto;
  }

  html.portal-auth-fullscreen #app,
  html.portal-auth-fullscreen .portal-app.v-application,
  html.portal-auth-fullscreen .portal-app .v-application__wrap,
  html.portal-auth-fullscreen .portal-app-chrome--auth {
    height: auto;
    min-height: 100vh;
    min-height: 100dvh;
  }

  html.portal-auth-fullscreen .portal-app .v-main {
    max-height: none;
    min-height: 100vh !important;
    min-height: 100dvh !important;
  }
}
/* Scrollbars finos y gris claro (misma línea que geclisa-site, p. ej. CustomTextArea / laboratorio) */
html,
body,
#app,
.portal-app,
.portal-app * {
  scrollbar-width: thin;
  scrollbar-color: #bdbdbd #f1f1f1;
}

html::-webkit-scrollbar,
body::-webkit-scrollbar,
#app::-webkit-scrollbar,
.portal-app::-webkit-scrollbar,
.portal-app *::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

html::-webkit-scrollbar-track,
body::-webkit-scrollbar-track,
#app::-webkit-scrollbar-track,
.portal-app::-webkit-scrollbar-track,
.portal-app *::-webkit-scrollbar-track {
  background: #f1f1f1;
}

html::-webkit-scrollbar-thumb,
body::-webkit-scrollbar-thumb,
#app::-webkit-scrollbar-thumb,
.portal-app::-webkit-scrollbar-thumb,
.portal-app *::-webkit-scrollbar-thumb {
  background-color: #bdbdbd;
  border-radius: 8px;
}

html::-webkit-scrollbar-thumb:hover,
body::-webkit-scrollbar-thumb:hover,
#app::-webkit-scrollbar-thumb:hover,
.portal-app::-webkit-scrollbar-thumb:hover,
.portal-app *::-webkit-scrollbar-thumb:hover {
  background-color: #9e9e9e;
}

.portal-app--dark,
.portal-app--dark * {
  scrollbar-color: #546170 #111820;
}

.portal-app--dark::-webkit-scrollbar-track,
.portal-app--dark *::-webkit-scrollbar-track {
  background: #111820;
}

.portal-app--dark::-webkit-scrollbar-thumb,
.portal-app--dark *::-webkit-scrollbar-thumb {
  background-color: #546170;
}

.portal-app--dark::-webkit-scrollbar-thumb:hover,
.portal-app--dark *::-webkit-scrollbar-thumb:hover {
  background-color: #697789;
}
</style>
